import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isEmpty } from "lodash";
import { observer } from "mobx-react-lite";
import { useEffectOnce, useInput } from "@lib-shared/hooks";
import { appSettings } from "core/config";
import { BusinessPage, ButtonText, Messages } from "core/constants";
import { AppContext } from "core/context";
import { SelfServiceContext } from "core/context/self-service.context";
import { logApiError } from "core/lib";
import getTextWidth from "core/lib/get-text-width";
import { MahPaymentRequest } from "core/models/mah-payment.request";
import { AmountToPayService, PaymentService } from "core/services/cef";
import { CustomerAccountsResponse, CustomerAccountsResponseWrapper } from "core/services/cef/amount-to-pay.service";
import PaymentStore, { PaymentAmount } from "core/stores/payment.store";
import PaymentsStore from "core/stores/payments.store";
import { ModalAlert, ModalIcon } from "components/common-ui";

const loadingMessages = {
    balance: "Getting account balance",
    subscriptions: "Subscriptions are still loading",
    initiating: "Initiating payment",
    redirecting: "You are being redirected to the payment gateway"
};

const Payment = observer(({ store }: { store: PaymentStore }) => {
    const [updateMode, setUpdateMode] = useState<boolean>(false);

    const inputValidation = (value: string) => {
        if (value.trim().length === 0) {
            return false;
        }

        if (value.startsWith("-")) {
            return false;
        }

        const parsed = parseFloat(value);

        if (isNaN(parsed) || parsed <= 0) {
            return false;
        }

        return true;
    }

    const amountInput = useInput(inputValidation);

    const appContext = useContext(AppContext);
    const user = appContext.user;

    const selfServiceContext = useContext(SelfServiceContext);
    const subscriptionsStore = selfServiceContext.stores.subscriptions;
    // const store = selfServiceContext.amountToPayStore;

    let existingDialog: HTMLDialogElement | null = null;

    const pageTitle = "- Payment";

    const navigate = useNavigate();

    const goBackToDashboard = () => navigate(BusinessPage.dashboard);

    const closeExistingModal = () => {
        if (existingDialog) {
            ModalAlert.close(existingDialog);
        }
    }

    const showErrorModal = (description = Messages.errorInitiatingPayment,
        textForButton = ButtonText.tryAgain,
        title = "Error") => {
        closeExistingModal();

        store.updateShowError(true);

        const options: any = {
            title: title,
            body: description,
            buttonText: textForButton,
            icon: ModalIcon.error
        };

        switch (textForButton) {
            case ButtonText.goBackToDashboard:
                options["onClose"] = () => goBackToDashboard();
                break;

            case ButtonText.tryAgain:
                options["onClose"] = () => tryAgain();
                options["showCancel"] = true;
                options["onCancel"] = () => goBackToDashboard();
                break;

            default: break;
        }

        existingDialog = ModalAlert.error(description, title, options);
    }

    const showLoader = (message: string) => {
        closeExistingModal();
        existingDialog = ModalAlert.loading(message);
    }

    const showWaitingToLoadModal = () => showLoader(loadingMessages.subscriptions);

    const showAccountBalanceModal = () => showLoader(loadingMessages.balance);

    const showLoadingModal = () => showLoader(loadingMessages.initiating);

    const showPaymentInitiatedModal = () => showLoader(loadingMessages.redirecting);

    const createPaymentRequest = () => {
        const currency = store.amountToPay?.currencyCode.toLowerCase();

        const primaryService = subscriptionsStore.primarySmartCard;

        if (isEmpty(primaryService)) {
            console.error("No primary service");
            showErrorModal("You have no subscriptions");
            return null;
        }

        const details = user?.company;

        const amount = parseFloat(amountInput.value);

        const requestOptions = {
            currency: currency ?? "",
            smartCardNumber: primaryService.smartCardNumber,
            firstName: details?.firstName ?? "", lastName: details?.lastName ?? "",
            amount: amount,
            languageCode: "EN",
            accountNumber: details?.customerNumber ?? 0,
            productNamesPrices: primaryService.productName,
            dueDate: store.amountToPay?.dueDate
        };

        return new MahPaymentRequest(requestOptions);
    }

    const makePayment = async () => {
        const amountToPayManager = selfServiceContext.managers.amountToPay;

        if (subscriptionsStore.isLoading) {
            showWaitingToLoadModal();
            return;
        }

        if (!store.hasAmountToPayBeenSet) {
            await amountToPayManager.populateAsync();
        }

        if (!appSettings.isEnabled.overPaying && !store.hasAmountToPayBeenSet) {
            showErrorModal("There is nothing to pay");
            return;
        }

        const paymentRequest = createPaymentRequest();

        if (!paymentRequest) {
            return;
        }

        try {
            showLoadingModal();

            const payApi = new PaymentService();
            const response = await payApi.makePayment(paymentRequest);

            const paymentResponse = response.data.paymentResponse.paymentResponseDetail;

            PaymentsStore.transactionId.set(paymentResponse.transactionId);

            showPaymentInitiatedModal();

            amountToPayManager.reset();

            window.location.assign(paymentResponse.redirectUrl);
        } catch (error: any) {
            logApiError(error);

            showErrorModal();
        }
    }

    const closeModal = () => {
        if (existingDialog != null) {
            ModalAlert.close(existingDialog);
        }
    }

    const payNow = () => makePayment();

    const handleAmountToPayResponse = (response: CustomerAccountsResponse) => {
        const data = new CustomerAccountsResponseWrapper(response);

        const amount = data.toPaymentAmount();

        amountInput.setValue(amount.amount.toFixed(2));

        store.updateAmountToPay(amount);

        store.updateIsLoaded(true);

        closeModal();
    }

    const getAccountBalance = async () => {
        try {
            const response = await new AmountToPayService().getCustomerAccounts();
            handleAmountToPayResponse(response?.data);
        } catch (error) {
            logApiError(error);

            showErrorModal("There has been an error retrieving your account balance. Please try again.", ButtonText.tryAgain);
        }
    }

    const onLoad = () => {
        if (!user?.can.makePayment) {
            ModalAlert.permissionsError(() => navigate(BusinessPage.dashboard));
            return;
        }

        showAccountBalanceModal();

        appContext.updateTitle(pageTitle);
        appContext.updateHeader("Your Account");

        getAccountBalance();
    }

    useEffectOnce(() => {
        store.reset();
        onLoad();
    });

    const tryAgain = () => {
        store.updateShowError(false);
        store.updateIsLoaded(false);

        showLoadingModal();
        getAccountBalance();
    }

    if (store.showError || !store.isLoaded) {
        return null;
    }

    const minAmountForWidthCalc = 1000;
    const amount = (store.amountToPay.amount < minAmountForWidthCalc
        ? minAmountForWidthCalc
        : store.amountToPay.amount).toFixed(2);

    const width = getTextWidth(amount, "bold 45px Poppins") + "px";
    const length = amount.toString().length + 1;

    const isValid = amountInput.isValid;

    const btnCssClassName = "btn " + (isValid ? "btn-primary" : "btn-black disabled");

    const updateAmountToPay = (e: any) => {
        let value = e.target.value as string;

        if (value.includes("e")) {
            value = value.replace("e", "");
            e.target.value = value;
        }

        amountInput.valueChangeHandler(e);
        store.updateAmountToPayValue(parseInt(e.target.value));
    }

    return (
        <section className="mah-payment" >
            <h4>
                Amount to pay
            </h4>

            <p className="amount-to-pay">
                {store.amountToPay.currencySymbol}
                <input type="number"
                    size={length}
                    className="amount-to-pay"
                    value={amountInput.value}
                    disabled={!updateMode}
                    onChange={updateAmountToPay}
                    style={{ width: width }} />
                <i className="icon icon-pencil" onClick={() => setUpdateMode(true)} />
            </p>

            {!isValid && <p className="error-message">
                Please enter an amount to pay
            </p>}

            <button className={btnCssClassName} onClick={payNow} disabled={!isValid}>Pay Now</button>
        </section>
    );
});

export default Payment;
